/**
 * Padrões compartilhados: o painel usa para criar um webchat novo e o script de seed usa para
 * popular o emulador. Ter isso aqui evita dois "fluxos de exemplo" diferentes divergindo.
 */
import { CHAT_SURFACES, DEFAULT_CHAT_BACKGROUND } from './chatBackground.js';
import { DEFAULT_HANDLE, FALSE_HANDLE, TRUE_HANDLE } from './engine.js';
/**
 * Bolha e texto vêm do tema padrão, e não de literais soltos: o webchat novo nasce com as três cores
 * combinando. Trocar o tema padrão sem trocar estes dois valores era o caminho fácil para nascer com
 * bolha quase branca sobre canvas escuro.
 */
export function createDefaultAppearance(overrides = {}) {
    const surface = CHAT_SURFACES[DEFAULT_CHAT_BACKGROUND];
    return {
        title: 'Fale com a gente',
        subtitle: 'Respondemos em minutos',
        position: 'bottom-right',
        offsetX: 24,
        offsetY: 24,
        primaryColor: '#25D366',
        bubbleColor: surface.recommendedBubble,
        textColor: surface.recommendedText,
        typingDelayMs: 550,
        autoOpenDelayMs: 0,
        chatBackground: DEFAULT_CHAT_BACKGROUND,
        ...overrides,
    };
}
/** Fluxo mínimo de um webchat recém-criado: uma saudação e uma pergunta. */
export function createStarterFlow() {
    return {
        startNodeId: 'saudacao',
        nodes: [
            {
                id: 'saudacao',
                type: 'message',
                position: { x: 0, y: 0 },
                data: { label: 'Saudação', text: 'Olá! 👋 Tudo bem?' },
            },
            {
                id: 'nome',
                type: 'input',
                position: { x: 0, y: 160 },
                data: {
                    label: 'Nome',
                    prompt: 'Como você se chama?',
                    saveAs: 'nome',
                    inputType: 'text',
                    placeholder: 'Seu nome',
                },
            },
            {
                id: 'fim',
                type: 'message',
                position: { x: 0, y: 320 },
                data: { label: 'Fim', text: 'Prazer, {{nome}}! Já vamos te chamar.' },
            },
        ],
        edges: [
            { id: 'e-saudacao-nome', source: 'saudacao', target: 'nome', sourceHandle: DEFAULT_HANDLE },
            { id: 'e-nome-fim', source: 'nome', target: 'fim', sourceHandle: DEFAULT_HANDLE },
        ],
    };
}
/**
 * A conversa de referência do planejamento: nome → WhatsApp → botões de opção, terminando no
 * hand-off para o WhatsApp.
 *
 * Nota de escopo: o catálogo de 7 blocos não tem um bloco "abrir WhatsApp", então o hand-off
 * sai por um bloco de código no navegador em modo `page` (precisa de `window.open`, o que o
 * sandbox não tem). Um 8º bloco dedicado resolveria isso com um clique em vez de código.
 */
export function createReferenceVersion(whatsappNumber = '5511999999999') {
    return {
        appearance: createDefaultAppearance({
            title: 'Atendimento',
            subtitle: 'Fale com um especialista',
        }),
        flow: {
            startNodeId: 'boas-vindas',
            nodes: [
                {
                    id: 'boas-vindas',
                    type: 'message',
                    position: { x: 0, y: 0 },
                    data: { label: 'Boas-vindas', text: 'Olá! Seja bem-vindo(a) 👋' },
                },
                {
                    id: 'pede-nome',
                    type: 'input',
                    position: { x: 0, y: 150 },
                    data: {
                        label: 'Nome',
                        prompt: 'Para começar, qual é o seu nome?',
                        saveAs: 'nome',
                        inputType: 'text',
                        placeholder: 'Digite seu nome',
                    },
                },
                {
                    id: 'pede-whatsapp',
                    type: 'input',
                    position: { x: 0, y: 300 },
                    data: {
                        label: 'WhatsApp',
                        prompt: 'Prazer, {{nome}}! Qual o seu WhatsApp com DDD?',
                        saveAs: 'whatsapp',
                        inputType: 'phone',
                        placeholder: '(11) 99999-9999',
                    },
                },
                {
                    id: 'escolhe-assunto',
                    type: 'buttons',
                    position: { x: 0, y: 450 },
                    data: {
                        label: 'Assunto',
                        prompt: 'Sobre o que você quer falar?',
                        saveAs: 'assunto',
                        options: [
                            { id: 'vendas', label: 'Quero comprar' },
                            { id: 'suporte', label: 'Preciso de suporte' },
                            { id: 'outro', label: 'Outro assunto' },
                        ],
                    },
                },
                {
                    id: 'checa-suporte',
                    type: 'condition',
                    position: { x: 0, y: 600 },
                    data: {
                        label: 'É suporte?',
                        mode: 'builder',
                        combinator: 'and',
                        rules: [{ variable: 'assunto', operator: 'eq', value: 'Preciso de suporte' }],
                    },
                },
                {
                    id: 'msg-suporte',
                    type: 'message',
                    position: { x: -260, y: 750 },
                    data: {
                        label: 'Resposta suporte',
                        text: 'Entendi, {{nome}}. Vou te levar direto para o time de suporte.',
                    },
                },
                {
                    id: 'msg-vendas',
                    type: 'message',
                    position: { x: 260, y: 750 },
                    data: {
                        label: 'Resposta comercial',
                        text: 'Perfeito, {{nome}}! Um especialista já vai falar com você.',
                    },
                },
                {
                    id: 'abre-whatsapp',
                    type: 'browserCode',
                    position: { x: 0, y: 900 },
                    data: {
                        label: 'Abrir WhatsApp',
                        executionMode: 'page',
                        saveAs: 'handoff',
                        timeoutMs: 3000,
                        code: [
                            '// Modo `page` porque `window.open` não existe dentro do sandbox.',
                            '// Só roda em domínio autorizado — o servidor nem entrega a config fora da allowlist.',
                            `const numero = '${whatsappNumber}';`,
                            "const texto = `Olá! Sou ${vars.nome} (${vars.whatsapp}). Assunto: ${vars.assunto}`;",
                            'const url = `https://wa.me/${numero}?text=${encodeURIComponent(texto)}`;',
                            'window.open(url, "_blank", "noopener");',
                            'return url;',
                        ].join('\n'),
                    },
                },
            ],
            edges: [
                { id: 'e1', source: 'boas-vindas', target: 'pede-nome', sourceHandle: DEFAULT_HANDLE },
                { id: 'e2', source: 'pede-nome', target: 'pede-whatsapp', sourceHandle: DEFAULT_HANDLE },
                { id: 'e3', source: 'pede-whatsapp', target: 'escolhe-assunto', sourceHandle: DEFAULT_HANDLE },
                { id: 'e4', source: 'escolhe-assunto', target: 'checa-suporte', sourceHandle: 'vendas' },
                { id: 'e5', source: 'escolhe-assunto', target: 'checa-suporte', sourceHandle: 'suporte' },
                { id: 'e6', source: 'escolhe-assunto', target: 'checa-suporte', sourceHandle: 'outro' },
                { id: 'e7', source: 'checa-suporte', target: 'msg-suporte', sourceHandle: TRUE_HANDLE },
                { id: 'e8', source: 'checa-suporte', target: 'msg-vendas', sourceHandle: FALSE_HANDLE },
                { id: 'e9', source: 'msg-suporte', target: 'abre-whatsapp', sourceHandle: DEFAULT_HANDLE },
                { id: 'e10', source: 'msg-vendas', target: 'abre-whatsapp', sourceHandle: DEFAULT_HANDLE },
            ],
        },
    };
}
//# sourceMappingURL=defaults.js.map